import { Smartphone, Tablet, Laptop, Cpu, ArrowRight, Wrench, Gamepad2, GraduationCap } from 'lucide-react';

const ServicesSection = () => {
  const services = [
    {
      icon: Smartphone,
      title: 'Réparation Téléphone',
      description: 'iPhone, Samsung, Huawei... Écran cassé, batterie, caméra, vitre arrière. Toutes marques.',
      features: ['Écran', 'Vitre arrière', 'Batterie', 'Connecteur charge', 'Caméra'],
      image: '/hero-bg.webp',
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      accentColor: 'blue',
      contactMode: 'devis',
    },
    {
      icon: Tablet,
      title: 'Réparation Tablette',
      description: 'iPad, Samsung Galaxy Tab, tablettes Android. Expertise complète sur tous modèles.',
      features: ['Vitre tactile', 'Écran', 'Batterie', 'Boutons'],
      image: '/services/service-tablette.webp',
      gradient: 'from-purple-500 to-pink-600',
      bgGradient: 'from-purple-50 to-pink-50',
      accentColor: 'purple',
      contactMode: 'contact',
    },
    {
      icon: Laptop,
      title: 'Réparation Ordinateur',
      description: 'MacBook, PC portable, desktop. Diagnostic gratuit et devis transparent.',
      features: ['Écran', 'Clavier', 'Batterie', 'Disque dur'],
      image: '/services/service-ordinateur.webp',
      gradient: 'from-cyan-500 to-teal-600',
      bgGradient: 'from-cyan-50 to-teal-50',
      accentColor: 'cyan',
      contactMode: 'contact',
    },
    {
      icon: Gamepad2,
      title: 'Consoles & Manettes',
      description: 'PlayStation, Xbox, Nintendo Switch. Réparation de vos consoles et manettes.',
      features: ['Joy-Con drift', 'Joysticks', 'Boutons', 'Connecteurs'],
      image: '/services/service-consoles.webp',
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-50',
      accentColor: 'green',
      contactMode: 'contact',
    },
    {
      icon: Cpu,
      title: 'Micro-soudure',
      description: 'Réparations avancées au niveau composants. Expertise technique de haut niveau.',
      features: ['Connecteurs', 'Composants', 'Puces', 'Diagnostics avancés'],
      image: '/services/service-microsoudure.webp',
      gradient: 'from-orange-500 to-red-600',
      bgGradient: 'from-orange-50 to-red-50',
      accentColor: 'orange',
      contactMode: 'contact',
    },
    {
      icon: GraduationCap,
      title: 'Formations Réparation',
      description: 'Apprenez à réparer vos appareils ! Formations individuelles ou en groupe, tous niveaux.',
      features: ['Initiation', 'Perfectionnement', 'Micro-soudure', 'Diagnostic'],
      image: '/services/service-formations.webp',
      gradient: 'from-indigo-500 to-violet-600',
      bgGradient: 'from-indigo-50 to-violet-50',
      accentColor: 'indigo',
      contactMode: 'contact',
    },
  ];

  const scrollTo = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleServiceClick = (service) => {
    scrollTo(service.contactMode === 'devis' ? '#devis' : '#contact');
  };

  return (
    <section
      id="services"
      className="section-spacing relative overflow-hidden"
      data-testid="services-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 gradient-section-blue" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-gradient-to-br from-cyan-400/10 to-green-400/10 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full mb-6">
            <Wrench size={16} className="text-[#0052FF]" />
            <span className="text-[#0052FF] font-semibold text-sm uppercase tracking-wider" data-testid="services-label">
              Nos services
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="services-title">
            Expertise complète pour{' '}
            <span className="gradient-text">tous vos appareils</span>
          </h2>
          <p className="text-lg text-slate-600" data-testid="services-description">
            De la réparation simple à la micro-soudure avancée, notre équipe qualifiée 
            prend en charge tous types de pannes avec professionnalisme.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="services-grid">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group relative rounded-3xl overflow-hidden hover-lift hover-glow cursor-pointer bg-gradient-to-br ${service.bgGradient}`}
              onClick={() => handleServiceClick(service)}
              data-testid={`service-card-${index}`}
            >
              {/* Card glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
              
              {service.image && (
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={service.image}
                    alt={`${service.title} à Montpellier — AlloRéparations`}
                    loading="lazy"
                    decoding="async"
                    width="940"
                    height="650"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
                  
                  {/* Icon overlay */}
                  <div className={`absolute bottom-4 left-4 w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-lg shadow-${service.accentColor}-500/30`}>
                    <service.icon size={26} />
                  </div>
                  
                  {/* Popular badge */}
                  {index === 0 && (
                    <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-slate-900">
                      ⚡ Plus demandé
                    </div>
                  )}
                </div>
              )}
              
              <div className={`p-6 ${!service.image ? 'pt-8' : ''} bg-white/80 backdrop-blur-sm`}>
                {!service.image && (
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-lg mb-5`}>
                    <service.icon size={28} />
                  </div>
                )}
                
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-testid={`service-title-${index}`}>
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4" data-testid={`service-description-${index}`}>
                  {service.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-5">
                  {service.features.map((feature, fIndex) => (
                    <span
                      key={fIndex}
                      className={`px-3 py-1.5 bg-gradient-to-r ${service.bgGradient} text-slate-700 text-sm rounded-full font-medium border border-white/50`}
                      data-testid={`service-feature-${index}-${fIndex}`}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                
                <div className={`flex items-center font-semibold group-hover:gap-3 gap-2 transition-all`}>
                  <span className="bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                    {service.contactMode === 'devis' ? 'Obtenir un devis' : 'Prendre contact'}
                  </span>
                  <ArrowRight size={18} className="text-slate-700 transition-transform group-hover:translate-x-2" />
                </div>
              </div>
              
              {/* Border gradient on hover */}
              <div className={`absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-${service.accentColor}-300/50 transition-colors pointer-events-none`} />
            </div>
          ))}
        </div>

        {/* Bottom stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { number: '+2500', label: 'Réparations', icon: '🔧' },
            { number: '30 min', label: 'En express', icon: '⚡' },
            { number: '6 mois', label: 'Garantie', icon: '🛡️' },
            { number: '4.7/5', label: 'Avis Google', icon: '⭐' },
          ].map((stat, index) => (
            <div
              key={index}
              className="glass-card-colored rounded-2xl p-5 text-center hover-lift"
            >
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="text-2xl font-bold gradient-text">{stat.number}</div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
